import { ErrorToast } from '@services/utils/ToastUtils';
import { ApiError } from '../../enums/ApiError';
import { TurnDetailResource } from '../../types/TurnDetailResource';
import {
  GetToken,
  setAuthHeader,
  setAuthHeaderForFormData,
  setAuthHeaderWithCustomData,
} from '../utils';
import { GetApiUrlFromPath } from './ApiMethods';

export type ApiResponse<T> = {
  acknowledged: boolean;
  attempts: number;
  currentPage: number;
  errorMessage: string;
  statusCode: string;
  hasResources: boolean;
  isError: boolean;
  isRecoverable: boolean;
  modified: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  resources?: T;
  resource?: T;
};

export async function GetAllFromPath<T>(path: string): Promise<T[]> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);
  const responsedata = await response.json();

  return responsedata;
}

export async function GetOffersFromPath<T>(path: string): Promise<ApiResponse<T[]>> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);
  const responsedata = await response.json();

  return responsedata;
}

export async function GetTurnPortfoliosFromPath<T>(path: string): Promise<T[]> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);
  const responsedata = await response.json();

  return responsedata;
}
export async function GetTurnDetailsFromPath<T>(path: string): Promise<TurnDetailResource> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);
  const responsedata = await response.json();

  return responsedata;
}

export async function GetMultiFromPath<T>(path: string): Promise<T[]> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);
  if (!response.ok) {
    throw new Error(ApiError.DefaultMessage);
  }
  const responsedata = await response.json();

  return responsedata;
}

export async function GetOneFromPath<T>(
  path: string,
  isStoreCodeRequired: boolean = true,
): Promise<T> {
  return (await GetOneFromPathRawResponse(path, isStoreCodeRequired)).json();
}

export async function GetOneFromPathRawResponse(
  path: string,
  isStoreCodeRequired: boolean = true,
): Promise<Response> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);

  if (isStoreCodeRequired) {
    url = appendStoreCode(url);
  }

  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);

  if (response.status === 404) {
    throw new Error(ApiError.NotFound);
  }

  if (!response.ok) {
    throw new Error(ApiError.DefaultMessage);
  }

  return response;
}

export async function Post<T>(path: string, body: any): Promise<T> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);

  const responsedata = await response.json();
  return responsedata;
}

export async function PostWithCustomHeader<T>(
  path: string,
  body: any,
  customHeaderData: { key: string; value: string },
): Promise<T> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeaderWithCustomData(token, customHeaderData);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);

  const responsedata = await response.json();
  return responsedata;
}

export async function PostFormData(path: string, body: any): Promise<Response> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeaderForFormData(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'POST',
    body: body,
    headers: authHeader,
  });
  handleNotAuthorized(response);
  if (!response.ok) {
    throw `${response.status} - ${response.statusText || ''}`;
  }
  return response;
}

export async function PostWithVoidReturn(path: string, body: any): Promise<void> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);

  if (!response.ok) {
    throw Error(response.status.toString());
  }
  return;
}

export async function PostWithReturn(
  path: string,
  body: any,
  omitStoreCode?: boolean,
): Promise<any> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  if (!omitStoreCode) {
    url = appendStoreCode(url);
  }

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);

  if (!response.ok) {
    throw Error(response.status.toString());
  }
  return response;
}

export async function PostUsingQueryParameters<T>(path: string): Promise<T> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'POST',
    headers: authHeader,
  });
  handleNotAuthorized(response);

  if (!response.ok) {
    throw Error(response.statusText);
  }
  const responsedata = await response.json();
  return responsedata;
}

export async function PostWithResponse(path: string, body: any): Promise<any> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);
  const contentType = response.headers.get('content-type');

  if ((contentType && contentType.indexOf('application/json') !== -1) || response.ok) {
    return response;
  }
  throw new Error(response.status.toString());
}

export async function PutUsingQueryParameters<T>(path: string): Promise<void> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);
  const response = await fetch(url, {
    method: 'PUT',
    headers: authHeader,
  });
  handleNotAuthorized(response);

  if (!response.ok) {
    throw Error(response.statusText);
  }
  return;
}

export async function PutWithVoidReturn(path: string, body: any): Promise<void> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);
  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);

  if (!response.ok) {
    throw Error(response.statusText);
  }
  return;
}

export async function Delete<T>(path: string, body: any): Promise<T> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'DELETE',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') !== -1) {
    return await response.json();
  } else {
    return await new Promise<any>((resolve, reject) => {
      if (response.status === 200) {
        resolve({});
      } else {
        reject();
      }
    });
  }
}

export async function PutWithReturn(path: string, body: any): Promise<any> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);

  if (!response.ok) {
    throw new Error(response.status.toString());
  }
  return response;
}

export async function PutWithResponse(path: string, body: any): Promise<any> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);
  const contentType = response.headers.get('content-type');

  if ((contentType && contentType.indexOf('application/json') !== -1) || response.ok) {
    return response;
  }
  throw new Error(response.status.toString());
}

export async function Put<T>(path: string, body?: any): Promise<T> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.indexOf('application/json') !== -1) {
    return await response.json();
  } else {
    return await new Promise<any>((resolve, reject) => {
      if (response.status === 200) {
        resolve({});
      } else {
        reject();
      }
    });
  }
}

export async function GetAllDataFromPath<T>(path: string): Promise<T> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);
  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);
  const responsedata = (await response.json()) as T;

  return responsedata;
}

export function appendStoreCode(url: string): string {
  if (url.indexOf('?') !== -1) {
    url += '&';
  } else {
    url += '?';
  }

  const storeCode = localStorage.getItem('am.storeCode');

  if (storeCode) {
    url += `storeCode=${storeCode}`;

    const inactiveStore = localStorage.getItem('am.inactiveStore');
    if (inactiveStore && inactiveStore === 'true') {
      url += `&isActive=${!inactiveStore}`;
    }
  }

  return url;
}

function prepareUrlForGetPdf(path: string): [string, Headers] {
  let url = GetApiUrlFromPath(path);
  const userDetails = localStorage.getItem('user');
  const userJson = userDetails ? JSON.parse(userDetails) : userDetails;
  const token = userJson ? userJson.access_token : userJson;
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  return [url, authHeader];
}

export async function GetPDFFromPathURL(path: string) {
  const [url, authHeader] = prepareUrlForGetPdf(path);
  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);
  return await response.arrayBuffer();
}

export async function GetSampleCampaignPDFFromPathURL(path: string) {
  const [url, authHeader] = prepareUrlForGetPdf(path);
  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);
  return response;
}

export async function PostArrayResponse<T>(path: string, body: T): Promise<T[]> {
  let url = GetApiUrlFromPath(path);
  const userDetails = localStorage.getItem('user');
  const userJson = userDetails ? JSON.parse(userDetails) : userDetails;
  const token = userJson ? userJson.access_token : userJson;
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: authHeader,
  });
  handleNotAuthorized(response);
  const responsedata = await response.json();

  return responsedata;
}

export const getPrivateIncentiveLookUpData = async (
  lastName: string,
  vin: string,
  redemptionCode: string,
  brands: string[],
  privateIncentiveLookupActionType: string,
  leadId: string,
  leadDealerId: string,
) => {
  const brandParam = brands.join('&brands=');
  const endpoint =
    privateIncentiveLookupActionType === 'PrivateIncentiveLookupConquestSearch'
      ? 'GetDataForEeqConquestIncentiveLookUp'
      : 'GetDataForPrivateIncentiveLookUp';
  let path: string = `/eui/common/${endpoint}?lastName=${lastName}&brands=${brandParam}&privateIncentiveLookupType=${privateIncentiveLookupActionType}`;

  if (leadId != null && leadId != '' && leadDealerId) {
    path = path.concat(`&leadId=${leadId}&leadDealerId=${leadDealerId}`);
  } else if (redemptionCode) {
    path = path.concat(`&redemptionCode=${redemptionCode}`);
  } else if (vin) {
    path = path.concat(`&vin=${vin}`);
  }

  let url = GetApiUrlFromPath(path);
  url = appendStoreCode(url);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  const HttpNotFound = 404;

  const result = await fetch(url, {
    method: 'GET',
    headers: authHeader,
  });

  if (result.ok) {
    return await result.json();
  } else if (result.status == HttpNotFound) {
    return [];
  }
};

export const getPrivateIncentiveLookUpDataConquestDashboard = async (
  lastName: string,
  brands: string[],
  leadId: string,
  dealerId: string,
  privateIncentiveLookupActionType: string,
) => {
  const brandParam = brands.join('&brands=');
  const path = `/eui/common/GetDataForEeqConquestIncentiveLookUp?dealerId=${dealerId}&leadId=${leadId}&leadDealerId=${dealerId}&lastName=${lastName}&brands=${brandParam}&privateIncentiveLookupType=${privateIncentiveLookupActionType}`;
  let url = GetApiUrlFromPath(path);
  url = appendStoreCode(url);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  const HttpNotFound = 404;

  const result = await fetch(url, {
    method: 'GET',
    headers: authHeader,
  });

  if (result.ok) {
    return await result.json();
  } else if (result.status == HttpNotFound) {
    return [];
  } else {
    ErrorToast(
      `Failed to fetch private incentive PIL data: ${result.status}: ${result.statusText};`,
    );
  }
};

// Handle the API's response
function handleNotAuthorized(response: Response): void {
  // Redirect to the login page, in case of getting 401 status code when session out.
  if (response.status === 401) {
    window.location.href = 'logout';
  }
  if (response.status === 412 && checkIfPwa()) {
    window.location.href = 'pwaError';
  }
  if (response.status === 511) {
    window.location.href = 'unauthorized';
  }
  if (!response.url.includes('common/stores')) {
    setIpAuthorized(response);
  }
}

const checkIfPwa = (): boolean => {
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  return false;
};

const setIpAuthorized = (response: Response) => {
  const xIpAuthorized = response.headers.get('x-ip-authorized');
  localStorage.setItem(
    'ipAuthorized',
    xIpAuthorized !== null && xIpAuthorized === 'True' ? 'true' : 'false',
  );
  if (
    localStorage.getItem('isServiceAutoUIRefresh') &&
    localStorage.getItem('isServiceAutoUIRefresh') == 'true'
  ) {
    localStorage.removeItem('isServiceAutoUIRefresh');
  } else {
    window.dispatchEvent(new Event('storage'));
  }
};

export async function GetInventoryFromPath<T>(path: string): Promise<T[]> {
  let url = GetApiUrlFromPath(path);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  url = appendStoreCode(url);

  const response = await fetch(url, { headers: authHeader });
  handleNotAuthorized(response);
  const responsedata = await response.json();

  return responsedata;
}

export const getOfferedDealType = async (dealerId: string, vin: string) => {
  const path = `/dealsheet/offeredDealType?dealerId=${dealerId}&vin=${vin}`;

  let url = GetApiUrlFromPath(path);
  url = appendStoreCode(url);
  const token = GetToken('user');
  const authHeader = setAuthHeader(token);
  const HttpNotFound = 404;

  const result = await fetch(url, {
    method: 'GET',
    headers: authHeader,
  });

  if (result.ok) {
    return await result.json();
  } else if (result.status == HttpNotFound) {
    return null;
  }
};
